import { useEffect, useId, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import type { CreditCheckRequest, FunnelSnapshotResponse } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Checkbox,
  DatePicker,
  Input,
  Label,
  Spinner,
} from '@fluxora/ui';

import { adultDobCutoffIso, ageInYears } from '../utils/dob-format';
import { isNotImplemented, problemTypeOf } from '../api/bff-client';
import { useCreditCheckMutation } from '../api/credit.queries';
import {
  useSendEmailOtpMutation,
  useVerifyEmailOtpMutation,
} from '../api/email-verification.queries';
import { useFunnelSnapshotQuery } from '../api/funnel.queries';
import { CreditDecisionResult } from '../components/CreditDecisionResult';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { EmailOtpField } from '../components/EmailOtpField';
import { EMAIL_VERIFIED_TICK_ID, EmailVerifiedTick } from '../components/EmailVerifiedTick';
import { useFunnelCandidateId } from '../hooks/useFunnelCandidateId';
import { useResendCooldown } from '../hooks/useResendCooldown';
import { env } from '../../../config/env';
import { useUsageSelection } from '../hooks/useUsageSelection';
import {
  classifyCreditFailure,
  creditFailureCopy,
  creditFailureFocusFieldId,
} from '../services/credit-error.service';
import { buildPlanDetailPath } from '../services/funnel-rehydrate.service';
import {
  sanitizeDriversLicenseInput,
  validateDriversLicense,
  sanitizeSsnInput,
  seedIdentityFormFrom,
  rehydrateNoticeFor,
  rehydrateNoticeKeys,
  creditSubmitBlocker,
  validateIdentityForm,
  type IdentityForm,
  type IdentityFormErrors,
} from '../services/credit.service';
import { formatPhoneInput } from '../services/leadContact.service';
import {
  applyVerifyOutcome,
  canRequestOtpFor,
  challengeIssued,
  emailOtpPhase,
  isEmailProven,
  hasOutstandingCode,

  seedChallengeFrom,
  validateOtpCode,
  type EmailVerificationState,
} from '../services/email-verification.service';

/**
 * Identity, credit & deposit decision (E-SF05).
 *
 * Reached at `/plan/:slug/credit?candidate=<candidateId>` after serviceability
 * (E-SF03). Collects name/DOB/SSN with explicit FCRA + PUCT consent, proves control
 * of the email address, takes the information-accuracy undertaking, runs the credit
 * check, and renders one of three branches (approve / deposit / waived).
 *
 * ── EMAIL PROOF-OF-CONTROL GATES THE SUBMIT (E-SF05) ───────────────────────
 * `POST /bff/credit/check` REFUSES an address that has not been proven by the OTP,
 * server-side, and that is the actual control — this page's disabled button is a
 * courtesy so nobody is invited to press something that can only fail. The proven
 * address is tracked as an ADDRESS, not a flag, so editing the email field after
 * verifying re-blocks submission instead of shipping a request the server will 400.
 *
 * ── NO LOGIC HERE ──────────────────────────────────────────────────────────
 * Which of the OTP field's five states to show, whether the typed address is
 * mailable, whether the code is well-formed, how many seconds until a resend, how a
 * verify answer folds into state, and whether submit is allowed — all of it comes
 * from `email-verification.service` / `credit.service`. This file wires hooks to
 * props.
 *
 * ── SSN HANDLING ───────────────────────────────────────────────────────────
 * The SSN lives only in this component's local state, is submitted as a mutation
 * variable (never a query key → never cached), and is discarded after. It is
 * masked on screen (type="password", autoComplete off), never written to storage.
 * The BFF forwards it to Accounts server-side and returns a decision only.
 *
 * All five async states are handled: the form (idle), submitting (Spinner), error
 * (Alert — incl. an HONEST "not available yet" when the Accounts credit endpoint
 * is not yet wired, a 501), and success (the decision branches).
 */
export function CreditPage(): JSX.Element {
  const { t } = useTranslation();
  // The rehydrate snapshot — the ONE read the identity form seeds from — is awaited
  // HERE, before the form mounts. The form seeds its state once, lazily; mounting
  // it while this read is still in flight seeds it empty, and the name, phone and
  // email captured at the contact step never arrive (which is exactly what
  // happened once `FunnelHydrationGate` stopped prefetching the snapshot). A
  // refused read (no session, network) is not a reason to withhold the form: it
  // renders unseeded, as it would for a first-time visitor.
  const snapshot = useFunnelSnapshotQuery();
  if (snapshot.isPending) {
    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
        <div
          className="flex min-h-[40vh] items-center justify-center"
          data-testid="credit-hydrating"
        >
          <Spinner size="lg" label={t('shop.funnel.hydrating')} />
        </div>
      </main>
    );
  }
  return <CreditForm snapshot={snapshot.data} />;
}

interface CreditFormProps {
  /** The settled rehydrate snapshot; undefined when the read was refused. */
  readonly snapshot: FunnelSnapshotResponse | undefined;
}

function CreditForm({ snapshot }: CreditFormProps): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  // Primary source is FunnelProvider (resolved at the meter step); `?candidate=`
  // is a back-compat fallback.
  const candidateId = useFunnelCandidateId();
  // The chosen usage, carried back onto the plan-detail link so stepping back does
  // not drop it (the detail page would then re-headline at the 1000 kWh midpoint).
  const selection = useUsageSelection();

  const credit = useCreditCheckMutation();
  const sendOtp = useSendEmailOtpMutation();
  const verifyOtp = useVerifyEmailOtpMutation();

  // ⚠️ Lazily seeded ONCE. `seedIdentityFormFrom` owns the decision about what may
  // survive a refresh (in short: the PROVEN email address, and nothing else — no
  // SSN, no DOB, no name, no consent). Keeping that in the service means the rule is
  // reviewable in one place with a test per field.
  const [form, setForm] = useState<IdentityForm>(() => seedIdentityFormFrom(snapshot));
  const [errors, setErrors] = useState<IdentityFormErrors>({});
  // The live OTP challenge (server instants + last answer). The proven ADDRESS is not
  // here — it lives on `form.verifiedEmail`, so the submit gate has one source of truth.
  // E-SF19 P3 — seeded from the rehydrate snapshot, so a REFRESH does not forget an
  // outstanding code and spend one of three hourly sends re-requesting it. The
  // seed carries the two instants and the attempt count, never the pending
  // recipient (it is unproven), so the phase stays `idle` and the customer still
  // retypes their address. See `seedChallengeFrom`.
  const [challenge, setChallenge] = useState<EmailVerificationState>(() =>
    seedChallengeFrom(snapshot),
  );
  const [otpCode, setOtpCode] = useState('');
  const [otpCodeError, setOtpCodeError] = useState<string | undefined>(undefined);

  const setText =
    (field: 'firstName' | 'lastName' | 'phone') =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };
  /**
   * `DatePicker` hands back ISO (`YYYY-MM-DD`) whichever way the customer entered it
   * — typed in `mm-dd-yyyy` or picked from the calendar — and `''` while a typed
   * value is still partial or impossible. So there is nothing to parse here and no
   * display state to keep in step: the field owns the mask, this owns the form.
   */
  const setDob = (iso: string): void => {
    setForm((prev) => ({ ...prev, dateOfBirth: iso }));
  };

  /**
   * Whole years, for the §25.478(e) age-waiver offer only — `undefined` whenever the
   * DOB is absent or unparseable, which is the normal state after a refresh (the DOB
   * deliberately does not rehydrate). `applicableWaiverGrounds` then leaves the ground
   * offered rather than removing a statutory route on a missing field.
   */
  const customerAgeYears =
    form.dateOfBirth === '' ? undefined : ageInYears(form.dateOfBirth, new Date());

  /**
   * The i18n keys for the "your form arrived partly filled" notice, or null.
   *
   * The DECISION (which variant, and whether either) is `rehydrateNoticeFor` — a
   * regulated-copy judgement, so it lives in the service with a test per branch and
   * this file only renders it. `snapshot.offer` is the ONLY thing read off the offer
   * record here, and only as a boolean: it stays render-only, never a second source
   * of the prefilled values (those arrive on `identity`/`verifiedEmail`).
   */
  const noticeKind = rehydrateNoticeFor(form, snapshot?.offer != null);
  const noticeKeys = noticeKind === null ? null : rehydrateNoticeKeys(noticeKind);
  const setBool =
    (field: 'fcraAuthorization' | 'puctDepositDisclosure' | 'informationAccuracyUndertaking') =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setForm((prev) => ({ ...prev, [field]: event.target.checked }));
    };

  /**
   * The email field gets its own handler: a code typed for the PREVIOUS address is
   * meaningless once the address changes, so it is dropped with the keystroke.
   * Nothing else needs resetting — `emailOtpPhase` ties both the verified and the
   * challenged address to the field's current value, so an edit re-blocks submission
   * on its own (no effect, no cleanup).
   */
  /**
   * The SSN field gets its own handler because its value is constrained as it is
   * typed — digits only, at most 9. The rule lives in `sanitizeSsnInput`; this is
   * the wiring.
   */
  const handleSsnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const ssn = sanitizeSsnInput(event.target.value);
    setForm((prev) => ({ ...prev, ssn }));
  };

  /**
   * Same shape as the SSN handler, same reason: the licence number is constrained
   * as it is typed (`sanitizeDriversLicenseInput` — uppercase alphanumerics, at
   * most 20) so a lower-case or hyphenated entry never becomes a submit-time
   * error the customer has to decode. The rule lives in the service; this is the
   * wiring.
   */
  const handleDriversLicenseChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const driversLicenseNumber = sanitizeDriversLicenseInput(event.target.value);
    setForm((prev) => ({ ...prev, driversLicenseNumber }));
    // Clear on the first keystroke — an error that persists while the customer is
    // fixing the very thing it complains about reads as the form not listening.
    setErrors((prev) => ({ ...prev, driversLicenseNumber: undefined }));
  };

  /**
   * Check the licence when the customer LEAVES it, not only at submit.
   *
   * `Run credit check` stays disabled until the email is proven and three
   * consents are ticked, so a submit-only format error on a field this near the
   * top of the form is invisible through several minutes of work.
   */
  const handleDriversLicenseBlur = (): void => {
    setErrors((prev) => ({
      ...prev,
      ...validateDriversLicense(form.driversLicenseNumber),
    }));
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const email = event.target.value;
    setForm((prev) => ({ ...prev, email }));
    setOtpCode('');
    setOtpCodeError(undefined);
  };

  const handleSendOtp = (): void => {
    setOtpCodeError(undefined);
    if (candidateId === undefined) return;
    sendOtp.mutate(
      { candidateId, email: form.email.trim() },
      { onSuccess: (res) => setChallenge(challengeIssued(form.email, res)) },
    );
  };

  const handleVerifyOtp = (): void => {
    const validation = validateOtpCode(otpCode);
    setOtpCodeError(validation.error);
    if (!validation.ok || validation.code === undefined || candidateId === undefined) return;
    verifyOtp.mutate(
      { candidateId, email: form.email.trim(), code: validation.code },
      {
        onSuccess: (res) => {
          const applied = applyVerifyOutcome(challenge, form.email, res);
          setChallenge(applied.challenge);
          setForm((prev) => ({ ...prev, verifiedEmail: applied.verifiedEmail }));
        },
      },
    );
  };

  const handleSubmit = (): void => {
    const validation = validateIdentityForm(form, new Date(), {
      skipEmailGate: env.featureSkipEmailVerification,
    });
    setErrors(validation.errors);
    if (!validation.ok || validation.value === undefined || candidateId === undefined || slug === undefined) {
      return;
    }
    const request: CreditCheckRequest = {
      candidateId,
      planId: slug,
      identity: validation.value.identity,
      contact: validation.value.contact,
      consent: validation.value.consent,
    };
    credit.mutate(request);
  };

  // ⚠️ ORDER IS LOAD-BEARING: THESE RUN BEFORE THE EARLY RETURN BELOW.
  // `useResendCooldown` is a hook, so it must be called on every render. Moving it
  // after the `candidateId === undefined` return would call it conditionally and
  // break hook order the moment a candidate resolves (React would throw).
  const resendInSeconds = useResendCooldown(challenge.resendAvailableAt);
  // Whether a code the customer has not redeemed is still live. Recomputed each
  // render alongside the countdown, so a challenge that lapsed while the tab was
  // closed stops claiming to be outstanding.
  const codeStillOutstanding = hasOutstandingCode(challenge);
  // WHY the submit button is disabled, if it is. Declared beside the other
  // per-render derivations and ABOVE the early return, so it is computed on every
  // render like the OTP phase it depends on.
  const submitBlocker = creditSubmitBlocker(form, {
    skipEmailGate: env.featureSkipEmailVerification,
  });
  const [ssnVisible, setSsnVisible] = useState(false);

  const otpPhase = emailOtpPhase({
    challenge,
    verifiedEmail: form.verifiedEmail,
    email: form.email,
    isSending: sendOtp.isPending,
    hasSendError: sendOtp.isError,
  });

  // No resolved meter → send the shopper to the landing chooser to resolve it
  // first (SHOP-6: replaces the old "start from your address" warning). Covers a
  // legacy plan-first entry gracefully rather than dead-ending.
  if (candidateId === undefined) {
    return <Navigate to="/" replace />;
  }

  /**
   * State-aware, because this route renders TWO steps.
   *
   * On the decision screen the shopper's "previous page" is the identity form they
   * just submitted — not the plan detail two steps up the funnel, which is what a
   * fixed link sent them to. `credit.reset()` only clears the mutation result on the
   * client; the server keeps the decision (prospectUid, deposit record), so this
   * discards nothing.
   *
   * ⚠️ It deliberately does NOT re-run the check. Returning to the form leaves the
   * customer to press "Run credit check" again, which matters: each run is a real
   * bureau call, and `/bff/credit` carries a zero free-allowance CAPTCHA gate. A back
   * control that silently re-ran it would spend money on a mis-click.
   */
  const backLink = credit.isSuccess ? (
    <FunnelBackLink onBack={() => credit.reset()} label={t('shop.credit.backToDetails')} />
  ) : (
    <FunnelBackLink
      to={slug === undefined ? '/' : buildPlanDetailPath(slug, selection)}
      label={t('shop.credit.back')}
    />
  );

  // WHY the credit check refused, in terms this screen can act on. The rule is
  // in `services/`; the page only renders the answer and moves focus to it.
  const creditFailure = classifyCreditFailure(problemTypeOf(credit.error));
  const creditCopy = creditFailureCopy(creditFailure);

  /*
   * Send the customer to the field they have to fix.
   *
   * An alert that says "check the number you entered" is only useful if they can
   * find it. A screen-reader user hears it at the foot of a six-input form with
   * no way to know which input it means — moving focus IS the answer for them,
   * and costs a sighted user nothing.
   *
   * Keyed on the failure rather than on `isError`, so a repeat submission that
   * fails the same way re-focuses, while an unrelated re-render does not steal
   * focus from wherever the customer has moved to. Guarded on `isError` so a
   * successful retry never yanks focus backwards.
   */
  const focusFieldId = credit.isError ? creditFailureFocusFieldId(creditFailure) : null;
  useEffect(() => {
    if (focusFieldId === null) return;
    document.getElementById(focusFieldId)?.focus();
  }, [focusFieldId]);

  const err = (key: keyof IdentityFormErrors): JSX.Element | null =>
    errors[key] !== undefined ? <span className="text-xs text-danger">{t(errors[key] ?? '')}</span> : null;

  return (
    // ⚠️ `max-w-3xl`, NOT `max-w-6xl` (2026-09-21). At 6xl the step's panels —
    // which are `max-w-3xl` themselves — sat in the left 768px of a 1152px
    // column with ~384px of dead space beside them, so the page read as
    // "everything shoved left". The form is at most two columns and fits 3xl,
    // so the whole step is now ONE centred column: heading, form and the
    // deposit panels all start at the same x and the page is balanced.
    <main className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-10">
      {backLink}
      <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
        {t('shop.credit.heading')}
      </h1>
      {/*
        NO SUBHEADING HERE, DELIBERATELY. It used to restate two things the form
        already says at the point they matter:
          - "we run a credit check … (PUCT §25.478)" → the FCRA authorization and the
            PUCT deposit-disclosure checkboxes say this, and the customer has to tick
            them, so it is consent copy rather than a preamble.
          - "your SSN is encrypted in transit and never stored" → `credit.form.ssn.note`
            sits under the SSN field itself.
        Repeating a disclosure above the fold does not strengthen it; it just moves the
        reader's eye past the version they have to act on.
      */}

      {credit.isSuccess ? (
        <CreditDecisionResult
          candidateId={candidateId}
          decision={credit.data}
          customerAgeYears={customerAgeYears}
        />
      ) : (
        <Card variant="glass" padding="md">
          <CardBody className="flex flex-col gap-3 p-0">
            {/*
              The form arrived partly filled, and the customer is told WHY and what
              is still missing — rather than being left to wonder about two blank
              fields. Two routes in, two truthful variants:
                · REFRESH — a returning tab. The proven address came back from the
                  server, so pressing F5 does not spend another of only three hourly
                  OTP sends. The SSN and DOB deliberately did not come back.
                · OFFER — the customer clicked an agent's emailed offer. The name,
                  phone and confirmed address were seeded server-side from the quote.
              Which one (and whether either) is decided by `rehydrateNoticeFor`; this
              file only renders the outcome. It clears once the DOB or SSN is entered.
            */}
            {noticeKeys === null ? null : (
              <Alert variant="info" title={t(noticeKeys.title)} role="status">
                {t(noticeKeys.body)}
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-first" isRequired>{t('shop.credit.form.firstName.label')}</Label>
                <Input id="cr-first" name="firstName" aria-required autoComplete="given-name" value={form.firstName} onChange={setText('firstName')} />
                {err('firstName')}
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-last" isRequired>{t('shop.credit.form.lastName.label')}</Label>
                <Input id="cr-last" name="lastName" aria-required autoComplete="family-name" value={form.lastName} onChange={setText('lastName')} />
                {err('lastName')}
              </div>
            </div>

            {/*
              Same grid as the name row above, with a single child, so the DOB field is
              exactly as wide as "First name" instead of spanning the whole card. A
              10-character `mm-dd-yyyy` has no use for full width, and matching the
              column keeps the form's left edge rhythm intact.
            */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-dob" isRequired>{t('shop.credit.form.dob.label')}</Label>
                <DatePicker
                  id="cr-dob"
                  name="dateOfBirth"
                  value={form.dateOfBirth}
                  onChange={setDob}
                  format="mm-dd-yyyy"
                  /*
                    THE APPLICANT'S 18TH BIRTHDAY, not today. A retail electric
                    contract is a contract, so an under-18 date is not merely
                    implausible — it is ineligible, and greying those days out is
                    kinder than letting someone fill in the whole form and then be
                    refused at the button.

                    ⚠️ PRESENTATION ONLY. This field is a masked TEXT input, so a typed
                    or pasted `01-01-2015` never meets a disabled calendar cell. The
                    enforcement is `schemas/dob.schema.ts`, which derives its bound from
                    this same function so the two cannot drift.
                  */
                  max={adultDobCutoffIso(new Date())}
                  hasError={errors.dateOfBirth !== undefined}
                  placeholder={t('shop.credit.form.dob.placeholder')}
                  labels={{
                    calendar: t('shop.credit.form.dob.pickerAria'),
                    previousMonth: t('shop.credit.form.dob.prevMonth'),
                    nextMonth: t('shop.credit.form.dob.nextMonth'),
                    month: t('shop.credit.form.dob.monthSelect'),
                    year: t('shop.credit.form.dob.yearSelect'),
                  }}
                />
                {err('dateOfBirth')}
              </div>
              {/*
                Phone shares the DOB row, not the email row. Email owns a taller cell —
                it carries the OTP send/verify affordance underneath — so pairing phone
                with it left a ragged column. Both of these are single-line fields.
              */}
              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-phone" isRequired>{t('shop.credit.form.phone.label')}</Label>
                <Input
                  id="cr-phone"
                  name="phone"
                  aria-required
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  // Same mask as the contact step: digits only, ten at most,
                  // grouped `214 555 0134` as they are typed. A pasted or
                  // autofilled `+1 …` loses its country code; a typed one does
                  // not, so the validation message points at what is on screen.
                  maxLength={12}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, phone: formatPhoneInput(event.target.value, prev.phone) }))
                  }
                />
                <span className="text-[11px] text-foreground/50">{t('shop.credit.form.phone.note')}</span>
                {err('phone')}
              </div>
            </div>

            {/* Email and SSN share one row — the two identity anchors side by side. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-email" isRequired>{t('shop.credit.form.email.label')}</Label>
                {/*
                  The proven-address tick sits INSIDE the field, at the right edge —
                  `relative` wrapper here, `absolute` on the tick. `pr-9` reserves the
                  room so a long address never runs underneath it. (`Input`'s `cn` is
                  plain clsx, not tailwind-merge, but Tailwind emits `pr-*` after
                  `px-*`, so the override holds — the same mechanism `DatePicker` uses
                  for its calendar trigger.)
                */}
                <div className="relative">
                  <Input
                    id="cr-email"
                    name="email"
                    aria-required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className="pr-9"
                    value={form.email}
                    onChange={handleEmailChange}
                    // Conditional so the idref can never dangle: the tick renders
                    // nothing unless the phase is `verified`.
                    aria-describedby={isEmailProven(otpPhase) ? EMAIL_VERIFIED_TICK_ID : undefined}
                  />
                  <EmailVerifiedTick phase={otpPhase} />
                </div>
                {err('email')}
                {/*
                  Proof of control, BEFORE the bureau call and the SSN. Every notice
                  this enrollment ever sends goes to this address, including the
                  statutory FCRA adverse-action notice — a typo here is a real
                  enrollment whose paper trail is delivered to a stranger.
                */}
                {env.featureSkipEmailVerification ? null : (
                  <EmailOtpField
                    phase={otpPhase}
                    code={otpCode}
                    onCodeChange={setOtpCode}
                    onSend={handleSendOtp}
                    onVerify={handleVerifyOtp}
                    resendInSeconds={resendInSeconds}
                    hasOutstandingCode={codeStillOutstanding}
                    canSend={canRequestOtpFor(form.email)}
                    isVerifying={verifyOtp.isPending}
                    lastOutcome={challenge.lastOutcome}
                    attemptsRemaining={challenge.attemptsRemaining}
                    codeError={otpCodeError}
                    sendError={sendOtp.error}
                    verifyError={verifyOtp.error}
                  />
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="cr-ssn" isRequired>{t('shop.credit.form.ssn.label')}</Label>
              {/*
                Masked + autoComplete off — the SSN is never stored client-side.

                The value is SANITISED on every keystroke (`sanitizeSsnInput`:
                digits only, capped at 9), so a symbol or a 10th digit simply never
                enters state. `maxLength` alone would bound the length but not the
                character set, and the field is masked — the customer cannot see a
                stray character to correct it. Pasting `123-45-6789` still works: the
                separators are stripped rather than the paste refused.

                Sanitising is NOT the rule, only a guard rail. Too-short is still
                caught by `validateSsn` on submit, and the BFF re-parses the
                contract's /^\d{9}$/u regardless — a caller that never runs our
                JavaScript is refused there.
              */}
              {/* Masked by DEFAULT, revealable on demand: nine hidden digits
                  are impossible to proof-read, and a mistyped SSN fails the
                  bureau pull. The reveal is the customer's own choice, on
                  their own screen — nothing is stored either way. */}
              <div className="relative">
                <Input
                  id="cr-ssn"
                  name="ssn"
                  type={ssnVisible ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={9}
                  className="pr-9"
                  value={form.ssn}
                  onChange={handleSsnChange}
                />
                <button
                  type="button"
                  aria-label={
                    ssnVisible
                      ? t('shop.credit.form.ssn.hide')
                      : t('shop.credit.form.ssn.show')
                  }
                  title={
                    ssnVisible
                      ? t('shop.credit.form.ssn.hide')
                      : t('shop.credit.form.ssn.show')
                  }
                  aria-pressed={ssnVisible}
                  onClick={() => setSsnVisible((v) => !v)}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-foreground/50 transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {ssnVisible ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" className="h-4 w-4">
                      <path d="M3 3l18 18" strokeLinecap="round" />
                      <path d="M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 9 4.5 10 7- .3.8-1 2-2.2 3.2M6.2 6.2C4 7.7 2.6 10 2 12c1 2.5 5 7 10 7 1.8 0 3.4-.5 4.8-1.3" strokeLinecap="round" />
                      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" className="h-4 w-4">
                      <path d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
                <span className="text-[11px] text-foreground/50">{t('shop.credit.form.ssn.note')}</span>
                {err('ssn')}
              </div>
            </div>

            {/*
              THE ALTERNATIVE IDENTIFIER. Not a radio group, and not a "don't have
              an SSN?" disclosure that hides one behind the other: both are simply
              on the form and at least one is required. An applicant may fill in
              either or both, and someone who has both to hand gives us
              corroborating evidence rather than an ambiguity.

              The separator line and the "or" label are doing the work a radio
              group would otherwise do — saying that these two blocks are
              alternatives — without making the customer commit to a branch before
              they have read what is in it.
            */}
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] uppercase tracking-wide text-foreground/50">
                {t('shop.credit.form.identity.or')}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="cr-dl">{t('shop.credit.form.driversLicense.label')}</Label>
              {/*
                NOT masked, unlike the SSN. A licence number is printed on a card
                the customer is holding and is transcribed character by character
                — hiding it guarantees typos — and it is a lower PII tier than the
                SSN (which is masked because it is recalled from memory and never
                checked back).

                Digits only, capped at 8: the Texas DPS format, enforced as it is
                typed. `autoComplete="off"` because a government ID number has no
                business in the browser's saved-form store, and no standard
                autofill token describes it.
              */}
              <Input
                id="cr-dl"
                name="driversLicenseNumber"
                type="text"
                autoComplete="off"
                spellCheck={false}
                inputMode="numeric"
                maxLength={8}
                value={form.driversLicenseNumber}
                onChange={handleDriversLicenseChange}
                onBlur={handleDriversLicenseBlur}
                hasError={errors.driversLicenseNumber !== undefined}
              />
              {err('driversLicenseNumber')}
            </div>

            <div className="flex flex-col gap-3 rounded-[14px] border border-border bg-surface p-4">
              <Checkbox
                name="fcraAuthorization"
                checked={form.fcraAuthorization}
                onChange={setBool('fcraAuthorization')}
                label={t('shop.credit.consent.fcra.label')}
                description={t('shop.credit.consent.fcra.copy')}
                hasError={errors.fcraAuthorization !== undefined}
              />
              {err('fcraAuthorization')}
              <Checkbox
                name="puctDepositDisclosure"
                checked={form.puctDepositDisclosure}
                onChange={setBool('puctDepositDisclosure')}
                label={t('shop.credit.consent.puct.label')}
                description={t('shop.credit.consent.puct.copy')}
                hasError={errors.puctDepositDisclosure !== undefined}
              />
              {err('puctDepositDisclosure')}
              {/*
                A THIRD, SEPARATE tick — never folded into the FCRA authorization
                above. Copy is provisional pending counsel; the version stamp of what
                was shown travels on the request and is deliberately never rendered.
              */}
              <Checkbox
                name="informationAccuracyUndertaking"
                checked={form.informationAccuracyUndertaking}
                onChange={setBool('informationAccuracyUndertaking')}
                label={t('shop.credit.consent.undertaking.label')}
                hasError={errors.informationAccuracyUndertaking !== undefined}
              />
              {err('informationAccuracyUndertaking')}
            </div>

            {/* ⚠️ The SAME predicate the button reads, so the sentence under it can
                never contradict its disabled state. See `creditSubmitBlocker`.

                ⚠️ DRAWN AS A CALLOUT, NOT AS HELPER TEXT. It shipped as a plain
                70%-opacity sentence in the same grey as every field note on the
                page, and it was read as one — customers still reported "the button
                is dead and nothing says why" while the sentence was on screen
                telling them why. A gate that blocks submission is a validation
                message and has to look like one, so it is a warning Alert sitting
                directly above the control it blocks.

                The `id` and `data-testid` stay on the announced element (the Alert
                itself) so `aria-describedby` still resolves for a screen reader —
                a disabled button is not focusable, so the association is the ONLY
                way the reason reaches one. */}


            <Button
              variant="primary"
              shape="pill"
              onClick={handleSubmit}
              disabled={credit.isPending || submitBlocker !== null}
            >
              {credit.isPending ? t('shop.credit.form.checking') : t('shop.credit.form.check')}
            </Button>

            {credit.isPending ? (
              <div className="flex justify-center py-2">
                <Spinner size="md" label={t('shop.credit.form.checking')} />
              </div>
            ) : null}

            {credit.isError ? (
              <Alert
                variant={isNotImplemented(credit.error) ? 'warning' : 'danger'}
                title={
                  isNotImplemented(credit.error)
                    ? t('shop.credit.error.title')
                    : t(creditCopy.titleKey)
                }
                role={isNotImplemented(credit.error) ? 'status' : 'alert'}
              >
                {isNotImplemented(credit.error)
                  ? t('shop.credit.error.notAvailable')
                  : t(creditCopy.bodyKey)}
              </Alert>
            ) : null}
          </CardBody>
        </Card>
      )}
    </main>
  );
}
